import React from 'react';
import { Box, Typography, Grid, useTheme, useMediaQuery } from '@mui/material';
import { Images } from '../../../utils/Images';
import { useDispatch, useSelector } from 'react-redux';
import { fetchCmsHomeAction } from '../../../store/slices/cmsSlice';

const MonthlySteps = () => {
  const theme = useTheme();
  
  // Custom media queries
  const isLargeScreen = useMediaQuery('(min-width: 1351px)');
  const isMediumScreen = useMediaQuery('(min-width: 641px) and (max-width: 1350px)');
  const isSmallScreen = useMediaQuery('(max-width: 640px)');
  const isMobileScreen = useMediaQuery('(max-width: 600px)');

  const dispatch = useDispatch();
  const cmsHome = useSelector((state) => state.cms.home);

  React.useEffect(() => {
    if (!cmsHome) dispatch(fetchCmsHomeAction());
  }, [cmsHome, dispatch]);

  const { sectionTitle, sectionDescription, stepTitles } = React.useMemo(() => {
    const data = Array.isArray(cmsHome?.data) ? cmsHome.data : [];
    const sec = data.find((s) => s?.id === 83);
    const rel = Array.isArray(sec?.relatedCMS) ? sec.relatedCMS : [];
    return {
      sectionTitle: sec?.title || '',
      sectionDescription: sec?.description || '',
      stepTitles: rel.slice(0, 4).map((r) => r?.title || ''),
    };
  }, [cmsHome]);

  // Steps data from CMS relatedCMS titles
  const steps = [
    { icon: Images.tap, stepNumber: 'Step 1', description: stepTitles[0] || '' },
    { icon: Images.creditCard, stepNumber: 'Step 2', description: stepTitles[1] || '' },
    { icon: Images.greenRupee, stepNumber: 'Step 3', description: stepTitles[2] || '' },
    { icon: Images.notification, stepNumber: 'Step 4', description: stepTitles[3] || '' },
  ];

  // Responsive styles
  const getResponsiveStyles = () => {
    if (isMobileScreen) {
      return {
        container: { width: '100%', padding: '0px 16px 0px 16px', margin: '0 auto' },
        title: { 
          fontSize: '18px', 
          lineHeight: '140%',
          letterSpacing: '-1%',
          marginBottom: '10px' 
        },
        subtitle: { 
          fontSize: '12px', 
          lineHeight: '20px', 
          letterSpacing: '0%',
          marginBottom: '20px' 
        },
        stepsContainer: { 
          display: 'flex', 
          flexDirection: 'column',
          gap: '16px',
          position: 'relative'
        },
        stepCard: {
          width: '100%',
          height: '80px',
          borderRadius: '8px',
          border: '0.5px solid #CCCCCC',
          padding: '12px',
          background: '#FFFFFF',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          position: 'relative',
          zIndex: 2
        },
        iconContainer: {
          width: '48px',
          height: '48px',
          borderRadius: '16px',
          padding: '8px',
          backgroundColor: '#F5F8ED',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0
        },
        iconSize: { width: '28px', height: '28px' },
        stepNumber: { fontSize: '12px', marginBottom: '2px' },
        stepDescription: { fontSize: '12px', lineHeight: '18px' },
        connectingLine: {
          position: 'absolute',
          left: '50%',
          top: '40px',
          height: 'calc(100% - 80px)',
          width: '2px',
          borderLeft: '2px dashed #A9C74E',
          transform: 'translateX(-50%)',
          zIndex: 1
        }
      };
    } else if (isSmallScreen) {
      return {
        container: { width: '94.44%', margin: '0 auto' },
        title: { fontSize: '28px', marginBottom: '14px' },
        subtitle: { fontSize: '15px', lineHeight: '22px', marginBottom: '35px' },
        stepsContainer: { 
          display: 'flex', 
          flexDirection: 'column',
          gap: '20px',
          alignItems: 'center',
          position: 'relative'
        },
        stepCard: {
          width: '280px',
          height: '85px',
          borderRadius: '10px',
          border: '0.5px solid #CCCCCC',
          padding: '12px',
          background: '#FFFFFF',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          position: 'relative',
          zIndex: 2
        },
        iconContainer: {
          width: '52px',
          height: '52px',
          borderRadius: '18px',
          padding: '10px',
          backgroundColor: '#F5F8ED',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0
        },
        iconSize: { width: '32px', height: '32px' },
        stepNumber: { fontSize: '13px', marginBottom: '3px' },
        stepDescription: { fontSize: '13px', lineHeight: '20px' },
        connectingLine: {
          position: 'absolute',
          left: '50%',
          top: '42px',
          height: 'calc(100% - 84px)',
          width: '2px',
          borderLeft: '2px dashed #A9C74E',
          transform: 'translateX(-50%)',
          zIndex: 1
        }
      };
    } else if (isMediumScreen) {
      return {
        container: { width: '94.44%', margin: '0 auto' },
        title: { fontSize: '32px', marginBottom: '15px' },
        subtitle: { fontSize: '15px', lineHeight: '23px', marginBottom: '40px' },
        stepsContainer: { 
          display: 'grid', 
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: '24px',
          position: 'relative'
        },
        stepCard: {
          width: '100%',
          height: '90px',
          borderRadius: '11px',
          border: '0.5px solid #CCCCCC',
          padding: '13px',
          background: '#FFFFFF',
          display: 'flex',
          alignItems: 'center',
          gap: '13px',
          position: 'relative',
          zIndex: 2
        },
        iconContainer: {
          width: '56px',
          height: '56px',
          borderRadius: '19px',
          padding: '11px',
          backgroundColor: '#F5F8ED',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0
        },
        iconSize: { width: '34px', height: '34px' },
        stepNumber: { fontSize: '13px', marginBottom: '3px' },
        stepDescription: { fontSize: '13px', lineHeight: '21px' },
        connectingLine: { display: 'none' } // Hide dashed line after 1350px
      };
    } else {
      return {
        container: { width: '94.44%', margin: '0 auto' },
        title: { fontSize: '36px', marginBottom: '16px' },
        subtitle: { fontSize: '16px', lineHeight: '24px', marginBottom: '40px' },
        stepsContainer: { 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          width: '100%',
          position: 'relative'
        },
        stepCard: {
          width: '315px',
          height: '98px',
          borderRadius: '12px',
          border: '0.5px solid #CCCCCC',
          padding: '14px',
          background: '#FFFFFF',
          display: 'flex',
          alignItems: 'center',
          gap: '14px',
          position: 'relative',
          zIndex: 2,
          flexShrink: 0
        },
        iconContainer: {
          width: '60px',
          height: '60px',
          borderRadius: '20px',
          padding: '12px',
          backgroundColor: '#F5F8ED',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          gap: '20px'
        },
        iconSize: { width: '36px', height: '36px' },
        stepNumber: { fontSize: '14px', marginBottom: '4px' },
        stepDescription: { fontSize: '14px', lineHeight: '22.65px' },
        connectingLine: {
          flex: 1,
          height: '1px',
          borderTop: '1px dashed #A9C74E',
          position: 'relative',
          zIndex: 1,
          alignSelf: 'center'
        }
      };
    }
  };

  const styles = getResponsiveStyles();

  return (
    <Box sx={{ ...styles.container, maxWidth: '1440px' }}>
      {/* Title */}
      <Typography
        sx={{
          fontWeight: 700,
          textAlign: 'center',
          verticalAlign: 'middle',
          textTransform: 'capitalize',
          color: '#1D1D1B',
          ...styles.title
        }}
      >
        {sectionTitle}
      </Typography>

      {/* Subtitle */}
      <Typography
        sx={{
          fontWeight: 400,
          textAlign: 'center',
          verticalAlign: 'middle',
          color: '#7D7D7D',
          maxWidth: '844px',
          margin: '0 auto',
          ...styles.subtitle
        }}
      >
        {sectionDescription}
      </Typography>

      {/* Steps Container */}
      <Box sx={styles.stepsContainer}>
        {/* Connecting line for mobile */}
        {(isMobileScreen || isSmallScreen) && (
          <Box sx={styles.connectingLine} />
        )}
        
        {steps.map((step, index) => (
          <React.Fragment key={index}>
            {/* Step Card */}
            <Box sx={styles.stepCard}>
              {/* Icon Container */}
              <Box sx={styles.iconContainer}>
                <img
                  src={step.icon}
                  alt={`${step.stepNumber} icon`}
                  style={{
                    objectFit: 'contain',
                    ...styles.iconSize
                  }}
                />
              </Box>

              {/* Text Content */}
              <Box sx={{ flex: 1 }}>
                <Typography
                  sx={{
                    fontWeight: 500,
                    lineHeight: '100%',
                    letterSpacing: '0%',
                    verticalAlign: 'middle',
                    textTransform: 'capitalize',
                    color: '#797E88',
                    ...styles.stepNumber
                  }}
                >
                  {step.stepNumber}
                </Typography>
                <Typography
                  sx={{
                    fontWeight: 600,
                    letterSpacing: '-2%',
                    verticalAlign: 'middle',
                    color: '#1D1D1B',
                    ...styles.stepDescription
                  }}
                >
                  {step.description}
                </Typography>
              </Box>
            </Box>

            {/* Connecting line for desktop (except for last item) */}
            {isLargeScreen && index < steps.length - 1 && (
              <Box sx={styles.connectingLine} />
            )}
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
};

export default MonthlySteps;